import React, { type ComponentType, type ReactNode, FormEvent, MouseEvent as ReactMouseEvent } from 'react';

import { DELIVERY_CASH } from 'apps/auth/constants/Constants';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PROFILE_BONUS_WITHDRAW, AUTH_SEND, AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { PHILIPPINES, VIETNAM } from 'apps/auth/constants/WellKnownCountries';
import { toLowerCase } from 'apps/auth/helpers/utils';

export const VIETCOMBANK = 'VIETCOMBANK';

type DisclaimerClickEvent = ReactMouseEvent<HTMLElement> | FormEvent<HTMLFormElement> | undefined;

export interface DisclaimerCountry {
    code?: string;
    id?: string | number;
}

export interface DisclaimerField {
    name?: string;
    value?: string;
    [key: string]: unknown;
}

type FieldCollection = Record<string, DisclaimerField | undefined> | DisclaimerField[] | undefined;

interface DisclaimerStateOptions {
    isBonus?: boolean;
    repeat?: boolean;
}

interface DisclaimerProps {
    toggleModal: (open: boolean, content?: ReactNode, closeOnOverlay?: boolean, disableCloseButton?: boolean) => void;
    stateOptions?: DisclaimerStateOptions;
}

interface DisclaimerComponentProps {
    formName: string;
    toCountry?: DisclaimerCountry | null;
    clickAgree: (event: DisclaimerClickEvent) => void;
    backLink: string;
    recipientType: string | null;
}

type DisclaimerComponent = ComponentType<DisclaimerComponentProps>;

const getFieldEntries = (fields?: FieldCollection): DisclaimerField[] => {
    if (!fields) {
        return [];
    }
    const entries = Array.isArray(fields) ? fields : Object.values(fields);
    return entries.filter((field): field is DisclaimerField => Boolean(field));
};

const showDisclaimer = (
    props: DisclaimerProps,
    setDisclaimerAgreed: (value: boolean) => void,
    toCountry: DisclaimerCountry | null,
    _toCurrency: unknown,
    Disclaimer: DisclaimerComponent,
    sendUriPostfix: string,
    incompleteTransfer?: boolean,
): void => {
    const isBonus = props.stateOptions?.isBonus;
    const isRepeat = props.stateOptions?.repeat;

    const getBackLink = (): string => {
        if (isBonus) {
            return `${AUTH_PROFILE_BONUS_WITHDRAW}/${toLowerCase(toCountry?.code ?? '')}/delivery${sendUriPostfix}`;
        }
        if (isRepeat || incompleteTransfer) {
            return AUTH_SEND_MAIN_LINK;
        }
        return `${AUTH_SEND}/${toLowerCase(toCountry?.code ?? '')}/delivery${sendUriPostfix}`;
    };

    const getRecipientType = (): string | null => {
        const match = sendUriPostfix.match(/=(.+)/);
        return match ? match[1] : null;
    };

    props.toggleModal(
        true,
        <Disclaimer
            formName={SEND_FORM_NAME}
            toCountry={toCountry ?? undefined}
            clickAgree={(e) => {
                e?.preventDefault();
                setDisclaimerAgreed(true);
                props.toggleModal(false);
            }}
            backLink={getBackLink()}
            recipientType={getRecipientType()}
        />,
        true,
        false,
    );
};

const canShowDisclaimerForVietnamCash = (
    isShowDisclaimer: boolean | undefined,
    toCountryId: string | number | undefined,
    deliveryMethod: string | undefined,
): boolean => Boolean(isShowDisclaimer && toCountryId === VIETNAM && deliveryMethod === DELIVERY_CASH);

const canShowDisclaimerForPhilCash = (
    isShowDisclaimer: boolean | undefined,
    toCountryId: string | number | undefined,
    deliveryMethod: string | undefined,
): boolean => Boolean(isShowDisclaimer && toCountryId === PHILIPPINES && deliveryMethod === DELIVERY_CASH);

export class DisclaimerService {
    static getFilledToBackCodeField = (fields?: FieldCollection): DisclaimerField | undefined =>
        getFieldEntries(fields).find((field) => field?.name === 'to_bank_code' && field?.value);

    static getFilledToAccBackCodeField = (fields?: FieldCollection): DisclaimerField | undefined =>
        getFieldEntries(fields).find((field) => field?.name === 'to_account_bank_code' && field?.value);

    static showDisclaimer = (
        props: DisclaimerProps,
        toCountry: DisclaimerCountry | null,
        toCurrency: unknown,
        isShowDisclaimer: boolean | undefined,
        deliveryMethod: string | undefined,
        setDisclaimerAgreed: (value: boolean) => void,
        Disclaimer: DisclaimerComponent,
        fields?: FieldCollection,
        sendUriPostfix = '',
        isDisclaimerFromServerSide?: boolean,
        incompleteTransfer?: boolean,
        toBankCodeRef?: DisclaimerField | null,
    ): void => {
        let isShow = Boolean(isShowDisclaimer);
        if (canShowDisclaimerForVietnamCash(isShowDisclaimer, toCountry?.id, deliveryMethod)) {
            const toBankCodeField = this.getFilledToBackCodeField(fields);
            const currentValue = toBankCodeField?.value;
            const previousValue = toBankCodeRef?.value;
            if (props.stateOptions?.repeat || incompleteTransfer) {
                isShow = Boolean(previousValue) && currentValue !== previousValue;
            } else {
                isShow = toBankCodeField !== undefined;
            }
        } else if (canShowDisclaimerForPhilCash(isDisclaimerFromServerSide, toCountry?.id, deliveryMethod)) {
            const toAccBankCodeField = this.getFilledToAccBackCodeField(fields);
            isShow = Boolean(toAccBankCodeField) && !props.stateOptions?.repeat && !incompleteTransfer;
        } else if (props.stateOptions?.repeat || incompleteTransfer) {
            isShow = false;
        }

        if (isShow) {
            showDisclaimer(props, setDisclaimerAgreed, toCountry, toCurrency, Disclaimer, sendUriPostfix, incompleteTransfer);
        }
    };

    static getDisclaimerText = (
        standardDisclaimer: string | undefined,
        specialDisclaimer: string | undefined,
        toCountryId: string | number | undefined,
        deliveryMethod: string | undefined,
        fields?: FieldCollection,
        isDisclaimerFromServerSide?: boolean,
        disclaimerFromServerSide?: string,
        specialVietcomBankDisclaimerFromServerSide?: string,
    ): string | undefined => {
        let toBankCode: DisclaimerField | undefined;
        if (toCountryId === VIETNAM && deliveryMethod === DELIVERY_CASH) {
            toBankCode = this.getFilledToBackCodeField(fields);
        }
        if (toBankCode?.value === VIETCOMBANK) {
            return isDisclaimerFromServerSide ? specialVietcomBankDisclaimerFromServerSide : specialDisclaimer;
        }
        return isDisclaimerFromServerSide ? disclaimerFromServerSide : standardDisclaimer;
    };

    static isShowVietnamCashDisclaimerAgain = (
        toCountry: DisclaimerCountry | null,
        isShowDisclaimer: boolean | undefined,
        deliveryMethod: string | undefined,
        previousToBankCode: DisclaimerField = {},
        fields?: FieldCollection,
    ): boolean => {
        if (canShowDisclaimerForVietnamCash(isShowDisclaimer, toCountry?.id, deliveryMethod)) {
            const currentValue = this.getFilledToBackCodeField(fields)?.value;
            const previousValue = previousToBankCode?.value;
            return currentValue !== previousValue;
        }
        return false;
    };
}
